import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { confirmAlert } from "react-confirm-alert";
import { errorMessage } from "../../util/ConfirmAlertUtil";
import { errorToast, success } from "../../util/ToastUtil";
import Loading from "../UI/Loading";
import {
  findByUsuarioParaAndFinalizado,
  findByUsuarioPara,
  findByUsuarioPorAndFinalizado,
  findByUsuarioPor,
  save,
  finalizar,
  removeTarefaById,
} from "./api-tarefa";
import TarefaForm from "./TarefaForm";
import TarefaList from "./TarefaList";
import TarefaProfile from "./TarefaProfile";

export const toUserKey = "to-user";
export const forUserKey = "for-user";

const NAO_FINALIZADO = "nao-finalizado";
const FINALIZADO = "finalizado";
const TODOS = "todos";

const FILTROS = [
  { value: NAO_FINALIZADO, label: "Não finalizadas" },
  { value: FINALIZADO, label: "Finalizadas" },
  { value: TODOS, label: "Todas" },
];

const EMPTY_PAGE = { content: [], size: 0, totalElements: 0, number: 0 };

const requestTarefas = (menu, filtro, page) => {
  const paraMim = menu === toUserKey;
  if (filtro === TODOS) {
    return paraMim ? findByUsuarioPara(page) : findByUsuarioPor(page);
  }
  const finalizado = filtro === FINALIZADO;
  return paraMim
    ? findByUsuarioParaAndFinalizado(page, finalizado)
    : findByUsuarioPorAndFinalizado(page, finalizado);
};

const TarefaContainer = () => {
  const [menu, setMenu] = useState(toUserKey);
  const [filtro, setFiltro] = useState(NAO_FINALIZADO);
  const [pageNumber, setPageNumber] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [tarefas, setTarefas] = useState(EMPTY_PAGE);
  const [tarefa, setTarefa] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAction, setIsLoadingAction] = useState(false);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    requestTarefas(menu, filtro, pageNumber)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message);
        } else {
          setTarefas(data);
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [menu, filtro, pageNumber, reloadKey]);

  const reloadFromStart = () => {
    setPageNumber(0);
    setReloadKey((key) => key + 1);
  };

  const updateTarefas = (data) => {
    setTarefas((prevState) => ({
      ...prevState,
      content: prevState.content.map((ps) => (ps.codigo === data.codigo ? data : ps)),
    }));
  };

  const handleMenuChange = (event, value) => {
    if (!value) return;
    setMenu(value);
    setPageNumber(0);
  };

  const handleFiltroChange = (event) => {
    setFiltro(event.target.value);
    setPageNumber(0);
  };

  const handleNovaTarefa = () => {
    // Tarefas novas são sempre atribuídas pelo usuário.
    if (menu !== forUserKey) {
      setMenu(forUserKey);
      setPageNumber(0);
    }
    setTarefa({ eNovo: true });
  };

  const clickSubmit = (event) => {
    event.preventDefault();
    if (!tarefa.descricao?.trim()) {
      errorToast("Informe a descrição.");
      return;
    }
    if (!tarefa.usuarioPara) {
      errorToast("Selecione para quem é a tarefa.");
      return;
    }
    if (tarefa.possuiPrazo && !tarefa.dataHoraPrazo) {
      errorToast("Informe a data e hora do prazo.");
      return;
    }

    const eNovo = tarefa.eNovo;
    setIsLoadingAction(true);
    save(tarefa)
      .then((data) => {
        if (!data) return;
        if (data.error) {
          errorMessage(data.message);
          return;
        }
        success("Salvo com sucesso.");
        setTarefa(data);
        if (eNovo) {
          reloadFromStart();
        } else {
          updateTarefas(data);
        }
      })
      .finally(() => setIsLoadingAction(false));
  };

  const removeTarefa = (tarefaSelected) => {
    setIsLoadingAction(true);
    removeTarefaById(tarefaSelected.codigo)
      .then((data) => {
        if (data?.error) {
          errorMessage(data.message);
          return;
        }
        setTarefa(null);
        setReloadKey((key) => key + 1);
        success("Tarefa removida.");
      })
      .finally(() => setIsLoadingAction(false));
  };

  const handleRemove = (tarefaSelected) => {
    confirmAlert({
      title: "Alerta",
      message: "Deseja remover a tarefa?",
      buttons: [
        { label: "Sim", onClick: () => removeTarefa(tarefaSelected) },
        { label: "Não", onClick: () => {} },
      ],
    });
  };

  const handleFinalizar = (codTarefa) => {
    setIsLoadingAction(true);
    finalizar(codTarefa)
      .then((data) => {
        if (!data) return;
        if (data.error) {
          errorMessage(data.message);
          return;
        }
        setTarefa(data);
        updateTarefas(data);
        success("Tarefa finalizada.");
      })
      .finally(() => setIsLoadingAction(false));
  };

  const renderDetail = () => (
    <>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <Tooltip title="Voltar para a lista">
          <IconButton edge="start" onClick={() => setTarefa(null)}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="subtitle1" fontWeight={600}>
          {tarefa.eNovo ? "Nova Tarefa" : menu === toUserKey ? "Tarefa" : "Editar Tarefa"}
        </Typography>
      </Stack>
      {menu === toUserKey ? (
        <TarefaProfile tarefa={tarefa} handleFinalizar={handleFinalizar} isLoadingAction={isLoadingAction} />
      ) : (
        <TarefaForm
          tarefa={tarefa}
          setTarefa={setTarefa}
          clickSubmit={clickSubmit}
          handleRemove={handleRemove}
          isLoadingAction={isLoadingAction}
        />
      )}
    </>
  );

  const renderList = () => (
    <>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1.5}
        mb={2}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Tarefas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {menu === toUserKey ? "Tarefas atribuídas a você." : "Tarefas que você atribuiu."}
          </Typography>
        </Box>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleNovaTarefa}>
          Nova Tarefa
        </Button>
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mb={2}>
        <ToggleButtonGroup size="small" exclusive value={menu} onChange={handleMenuChange}>
          <ToggleButton value={toUserKey}>Para mim</ToggleButton>
          <ToggleButton value={forUserKey}>Atribuídas por mim</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          select
          size="small"
          value={filtro}
          onChange={handleFiltroChange}
          aria-label="Filtro de tarefas"
          sx={{ minWidth: 180 }}
        >
          {FILTROS.map((f) => (
            <MenuItem key={f.value} value={f.value}>
              {f.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {isLoading ? (
        <Box py={5}>
          <Loading size={28} description="Carregando Tarefas..." />
        </Box>
      ) : (
        <TarefaList
          tarefas={tarefas}
          handleChangePage={(event, newPage) => setPageNumber(newPage)}
          setTarefa={setTarefa}
          atribuidasParaMim={menu === toUserKey}
        />
      )}
    </>
  );

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5 }}>
      {tarefa ? renderDetail() : renderList()}
    </Paper>
  );
};

export default TarefaContainer;
